/**
 * Resolução de **código → descrição** dos catálogos do produto (atividade
 * empresarial, tipo de construção, objeto segurado, etc.).
 *
 * Existe porque o InsureMO devolve no risco apenas o código (`"149"`, `"1"`) e
 * a UI precisa do nome. O mesmo `find(x => String(x.code) === code)?.description`
 * estava copiado em ~8 telas; toda tela nova que esquecia de copiar passava a
 * exibir o número cru — exatamente o bug do card de locais na análise de
 * proposta.
 *
 * Camada **pura** (sem React) para ser testável no ambiente `node` do vitest —
 * a camada de dados fica em `useCatalogLabels`.
 */

/** Catálogos disponíveis para tradução. Catálogo novo = uma entrada aqui. */
export type CatalogId =
  | 'business-activities'
  | 'construction-types'
  | 'insured-objects'
  | 'property-classifications'
  | 'fire-protection-rating-types'
  | 'theft-protection-rating-types';

/**
 * Shape comum dos catálogos. `code` é `number` em alguns (`BusinessActivity`,
 * proteções de incêndio/roubo) e `string` em outros (`ConstructionType`,
 * `InsuredObjectType`, `PropertyClassification`) — por isso a normalização com
 * `String()` dos dois lados é obrigatória, e não opcional.
 */
export type CatalogItem = {
  code: string | number;
  description?: string;
};

/** Índice `código normalizado → descrição` de um catálogo. */
export type CatalogIndex = Map<string, string>;

function normalizeCode(code: string | number | null | undefined): string {
  return String(code ?? '').trim();
}

/**
 * Indexa um catálogo por código normalizado. Itens sem código ou sem descrição
 * são descartados (não têm como traduzir nada).
 */
export function indexCatalogByCode(
  items: readonly CatalogItem[] | undefined
): CatalogIndex {
  const index: CatalogIndex = new Map();

  for (const item of items ?? []) {
    const code = normalizeCode(item?.code);
    const description = item?.description?.trim();
    if (code && description) index.set(code, description);
  }

  return index;
}

/**
 * Traduz um código, com a cadeia de fallback que o portal já usa nos demais
 * cards de risco: descrição do catálogo → descrição já resolvida pelo BFF →
 * **o código cru**.
 *
 * O código cru é o fallback certo (e não `'-'`): enquanto o catálogo carrega, o
 * usuário vê o número e ele é substituído pelo nome quando a query responde —
 * sem o flicker `código → traço → nome` e sem perder informação se o código não
 * estiver no catálogo. Devolve `''` só quando não há código nenhum, e aí quem
 * renderiza decide o placeholder.
 */
export function resolveCatalogLabel(
  index: CatalogIndex | undefined,
  code: string | number | null | undefined,
  fallbackDescription?: string
): string {
  const normalized = normalizeCode(code);
  const fromCatalog = normalized ? index?.get(normalized) : undefined;

  return fromCatalog || fallbackDescription?.trim() || normalized;
}

/**
 * Versão para campos multivalorados (ex.: "Objeto Segurado" vem de um
 * `scheduleExt` com um item por objeto). Mantém a ordem, remove vazios e
 * duplicados — mesma regra que o BFF aplica no PDF da cotação.
 */
export function resolveCatalogLabels(
  index: CatalogIndex | undefined,
  codes: ReadonlyArray<string | number | null | undefined>,
  separator = ', '
): string {
  const labels: string[] = [];

  for (const code of codes) {
    const label = resolveCatalogLabel(index, code);
    if (label && !labels.includes(label)) labels.push(label);
  }

  return labels.join(separator);
}
